var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { isValid, parse } from 'date-fns';
export const CellContextPasteAction = function (_key, [selection]) {
    return __awaiter(this, void 0, void 0, function* () {
        const clipboardRawContents = yield navigator.clipboard.readText();
        const clipboard = clipboardRawContents.split('\n').map((line) => line.split('\t'));
        const patch = clipboard.reduce((acc, current, row) => {
            current.forEach((val, col) => {
                acc.push([row + selection.start.row, col + selection.start.col, val]);
            });
            return acc;
        }, []);
        this.setDataAtCell(patch);
    });
};
export const CellContextInsertNRowsAboveAction = (rowLimit = Infinity) => {
    return function (_key, [selection]) {
        return __awaiter(this, void 0, void 0, function* () {
            const rowsToInsert = Math.min(rowLimit - this.countRows(), getSelectedRowsCount(this.getSelectedRange()));
            this.alter('insert_row_above', selection.start.row, rowsToInsert);
        });
    };
};
export const CellContextInsertNRowsBelowAction = (rowLimit = Infinity) => {
    return function (_key, [selection]) {
        return __awaiter(this, void 0, void 0, function* () {
            const rowsToInsert = Math.min(rowLimit - this.countRows(), getSelectedRowsCount(this.getSelectedRange()));
            this.alter('insert_row_below', selection.end.row, rowsToInsert);
        });
    };
};
export const CellContextInsertDynamicLabel = (dir, rowLimit = Infinity) => {
    return function () {
        const rowsToInsert = Math.min(rowLimit - this.countRows(), getSelectedRowsCount(this.getSelectedRange()));
        return `Insert ${rowsToInsert} row${rowsToInsert > 1 ? 's' : ''} ${dir}`;
    };
};
export const isHeaderRow = function () {
    var _a;
    return ((_a = this.getSelectedRange()) === null || _a === void 0 ? void 0 : _a[0].from.row) === -1;
};
export const shouldHideInsertRows = (rowLimit = Infinity) => {
    return function () {
        const rowsToInsert = Math.min(rowLimit - this.countRows(), getSelectedRowsCount(this.getSelectedRange()));
        return rowsToInsert === 0;
    };
};
/**
 * This is a workaround for the fact that handsontable does not support cell values
 * other than primitives (i.e. string). For 'select' columns that we use Option
 * interface for outside of Spreadsheet, we're stringifying it to JSON before passing
 * to handsontable.
 *
 * SpreadsheetInputSelect then parses it back to Option for rendering SelectDropdownField.
 */
export function prepareDataset(data, columns, inPlace = false) {
    const selectColumns = columns.filter((c) => c.type === 'select');
    if (selectColumns.length === 0)
        return inPlace ? data : [...data];
    return data.map((row) => prepareRow(inPlace ? row : Object.assign({}, row), columns));
}
export function prepareRow(row, columns) {
    const selectColumns = columns.filter((c) => c.type === 'select');
    if (selectColumns.length === 0)
        return row;
    selectColumns.forEach((col) => {
        if (!col.data)
            return;
        const key = typeof col.data === 'function' ? col.data(row) : col.data;
        if (!row[key])
            return;
        if (!isJsonString(row[key])) {
            row[key] = JSON.stringify(row[key]);
        }
    });
    return row;
}
export function combineValidators(validators = []) {
    return (value, callback) => {
        const valid = validators.every((v) => !v(value));
        return callback(valid);
    };
}
export function getSelectedRowsCount(range) {
    const { to, from } = range === null || range === void 0 ? void 0 : range[0];
    return range ? Math.abs(to.row - (from.row === -1 ? 0 : from.row)) + 1 : 1;
}
export function getAnnotationByDefinition(definition) {
    if (definition.type === 'select' && definition.forceSelect) {
        return 'Must choose from list';
    }
}
export function stringToSnakeCase(string) {
    return string.toLowerCase().replace(/ /g, '_').replace(/,/g, '');
}
export function optionFromString(string) {
    return {
        label: string,
        value: string,
    };
}
export function isJsonString(string) {
    try {
        const json = JSON.parse(string);
        return Array.isArray(json) || typeof json === 'object';
    }
    catch (_a) {
        return false;
    }
}
/**
 * Basic change validation that should prevent incorrect cell changes, like
 * checkbox accepting string "abcdef".
 */
export function isChangeAllowed(column, next) {
    switch (column.type) {
        case 'checkbox':
            return typeof next === 'boolean';
        case 'date': {
            if (typeof next !== 'string' || !column.dateFormat)
                return false;
            const parsed = parse(next, column.dateFormat, new Date());
            return isValid(parsed);
        }
        case 'autocomplete':
        case 'dropdown': {
            /* New values creation allowed, we accept any change */
            if (!column.forceSelect || !next)
                return true;
            return (column.source || []).findIndex((o) => o === next) !== -1;
        }
        case 'select': {
            // next content was validated by normalizeChangedValue
            return typeof next === 'string' && isJsonString(next);
        }
        default:
            return true;
    }
}
function normalizeSelectOption(column, next) {
    if (Array.isArray(next)) {
        return next.map((option) => normalizeSelectOption(column, option)).filter((option) => !!option);
    }
    if (next === null || next === undefined) {
        return null;
    }
    const isOption = (next === null || next === void 0 ? void 0 : next.value) && (next === null || next === void 0 ? void 0 : next.label);
    const options = column.options || [];
    let value = isOption ? next.label : next;
    if (typeof value === 'string') {
        value = value.trim();
    }
    if (!value) {
        return null;
    }
    const found = options.find((o) => o.label.trim() === value);
    /* If such option exist, return it */
    if (found) {
        return found;
    }
    /* Otherwise, if column is 'creatable' (can add new values), create new Option */
    if (!column.forceSelect) {
        return isOption ? next : optionFromString(value);
    }
    return null;
}
/**
 * In some cases, when cell value changes, we want to parse it or change to a
 * different one - i.e. when changing 'select' type cell to string, before we
 * reject the change (since 'select' only accepts Option object(s)), we check
 * if such option label exists and then return the Option, not the string.
 */
export function normalizeChangedValue(column, next) {
    if (column.type === 'select') {
        let value = normalizeSelectOption(column, next);
        if (!value) {
            return null;
        }
        if (column.multiple && !Array.isArray(value)) {
            value = [value];
        }
        else if (!column.multiple && Array.isArray(value)) {
            value = value[0];
        }
        return JSON.stringify(value);
    }
    else if (column.type === 'checkbox') {
        if (typeof next !== 'boolean' && next !== '' && next !== null) {
            switch (next.toLowerCase()) {
                case 'true':
                    return true;
                case 'false':
                    return false;
                default:
                    return;
            }
        }
        return !!next;
    }
    if (typeof next === 'string') {
        next = next.trim();
    }
    return next;
}
//# sourceMappingURL=Spreadsheet.utils.js.map